const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** 날짜만 있는 문자열(YYYY-MM-DD)은 시간대 변환 없이 그대로 표시한다. */
export function formatDate(value?: string | null): string {
  if (!value) return '-';
  const dateOnly = DATE_ONLY_RE.exec(value);
  if (dateOnly) {
    return `${dateOnly[1]}.${dateOnly[2]}.${dateOnly[3]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
}

export function formatDateTime(value?: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}.${m}.${day} ${hh}:${mm}`;
}

export function formatRelativeTime(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  if (diff < 0) return formatDateTime(value);
  const min = Math.floor(diff / 60000);
  if (min < 1) return '방금 전';
  if (min < 60) return `${min}분 전`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}일 전`;
  return formatDate(value);
}

export function daysBetween(from?: string | null, to: Date = new Date()): number | null {
  if (!from) return null;
  const start = new Date(from);
  if (Number.isNaN(start.getTime())) return null;
  const ms = to.getTime() - start.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

export function formatElapsedDays(days: number | null): string {
  if (days === null) return '-';
  if (days < 0) return '수업 시작 전';
  if (days < 30) return `${days}일`;
  const months = Math.floor(days / 30);
  const rem = days % 30;
  return rem > 0 ? `${months}개월 ${rem}일` : `${months}개월`;
}

export function formatNumber(value?: number | null): string {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('ko-KR');
}

export function formatDday(target?: string | null): { label: string; tone: 'past' | 'soon' | 'far' } | null {
  if (!target) return null;
  const d = new Date(target);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.ceil((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return { label: '종료', tone: 'past' };
  if (diff === 0) return { label: 'D-Day', tone: 'soon' };
  if (diff <= 7) return { label: `D-${diff}`, tone: 'soon' };
  return { label: `D-${diff}`, tone: 'far' };
}

/**
 * 다음 교습비 결제일을 계산한다.
 * 지정한 '매월 n일'을 기준으로, 이번 달 해당 일이 아직 안 지났으면 이번 달,
 * 이미 지났으면 다음 달 같은 날짜를 로컬 기준(YYYY-MM-DD)으로 돌려준다.
 * toISOString()을 쓰면 UTC로 변환되면서 하루씩 밀리므로 로컬 날짜로 조립한다.
 */
export function nextDueDate(dueDay?: number | null): string | null {
  if (!dueDay || dueDay < 1 || dueDay > 31) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const thisMonth = new Date(today.getFullYear(), today.getMonth(), dueDay);
  if (thisMonth.getTime() < today.getTime()) {
    return toDateKey(new Date(today.getFullYear(), today.getMonth() + 1, dueDay));
  }
  return toDateKey(thisMonth);
}

export type TuitionStatus = 'paid' | 'overdue' | 'upcoming';

export interface TuitionStatusResult {
  status: TuitionStatus;
  cycleDue: string | null;
  nextDue: string | null;
}

function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * 현재 회차의 교습비 결제 상태를 계산한다.
 * - paid: 이번 회차 결제일 이후로 결제완료 처리됨
 * - overdue: 이번 회차 결제일이 지났는데 결제완료가 안 됨 (미납)
 * - upcoming: 아직 결제일이 오지 않음
 */
export function tuitionStatus(
  dueDay?: number | null,
  lastPaidAt?: string | null,
): TuitionStatusResult {
  const nextDue = nextDueDate(dueDay);
  if (!dueDay || dueDay < 1 || dueDay > 31) {
    return { status: lastPaidAt ? 'paid' : 'upcoming', cycleDue: null, nextDue };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const y = today.getFullYear();
  const m = today.getMonth();
  const thisMonthDue = new Date(y, m, dueDay);
  const cycleDue =
    today.getTime() >= thisMonthDue.getTime() ? thisMonthDue : new Date(y, m - 1, dueDay);

  const paidDate = lastPaidAt ? new Date(lastPaidAt) : null;
  const hasValidPaid = !!paidDate && !Number.isNaN(paidDate.getTime());

  if (hasValidPaid && (paidDate as Date).getTime() >= cycleDue.getTime()) {
    return { status: 'paid', cycleDue: toDateKey(cycleDue), nextDue };
  }
  if (today.getTime() > cycleDue.getTime()) {
    return { status: 'overdue', cycleDue: toDateKey(cycleDue), nextDue };
  }
  return { status: 'upcoming', cycleDue: toDateKey(cycleDue), nextDue };
}

const WEEK_ORDER = ['월', '화', '수', '목', '금', '토', '일'];

export interface WeekDay {
  day: string;
  date: string;
  isToday: boolean;
}

function toDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** 오프셋 주(0=이번 주, 1=다음 주...)의 월~일 날짜 목록을 돌려준다. */
export function getWeek(offset = 0): WeekDay[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffToMonday = (today.getDay() + 6) % 7;
  const monday = new Date(today);
  monday.setDate(today.getDate() - diffToMonday + offset * 7);
  return WEEK_ORDER.map((day, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return {
      day,
      date: toDateString(d),
      isToday: d.getTime() === today.getTime(),
    };
  });
}

export function getCurrentWeek(): WeekDay[] {
  return getWeek(0);
}

/** YYYY-MM-DD 문자열을 'n월 n일' 형태로 표시한다. */
export function formatMonthDay(dateKey: string): string {
  const d = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateKey;
  return `${d.getMonth() + 1}월 ${d.getDate()}일`;
}